import cloneDeep from 'lodash/cloneDeep.js';

import axios from '@/plugins/axios.js';
import moment from '@/plugins/vue-moment.js';

const namespace = name => {
    return name.substring(0, name.indexOf('/'));
};

const parseTimeString = (time, timezone) => {
    const data = moment.tz(time.toLocaleString(), 'M/D/Y, hh:mm:ss A', timezone);

    return `${data.format('HH:mm')}`;
};

export default {
    'SectionTemplates/EDIT': {
        fields: ['title', 'description', 'prerequisite_uuid'],
        callback: (store, mutation) => {
            const section = store.getters['SectionTemplates/get'](mutation.payload.uuid);

            if (!section) {
                return;
            }

            // Prevent persisting empty sections: require a non-empty title
            if (!section.title || (typeof section.title === 'string' && section.title.trim() === '')) {
                return;
            }

            store.dispatch('AutoSave/setSaving', mutation);

            store
                .dispatch('AutoSave/sections', {
                    mutation: mutation.type,
                    section,
                })
                .then(() => {
                    // After single section save, clear "newly created" styling by turning off editing
                    store.dispatch('SectionTemplates/updateItem', {
                        uuid: section.uuid,
                        field: 'displayStatus',
                        value: { editing: false },
                    });

                    // Refresh entire course to sync nested items and UI state
                    try {
                        const courseId = section.course_template_id;
                        if (courseId) {
                            // Delay the refresh slightly to allow displayStatus update to stick
                            setTimeout(() => {
                                axios.get(`/api/course-templates/${courseId}?menu=true`).then(resp => {
                                    if (resp.data?.data) {
                                        store.dispatch('CourseTemplateForm/hydrate', { payload: resp.data.data });

                                        // Re-apply the displayStatus after hydration
                                        setTimeout(() => {
                                            store.dispatch('SectionTemplates/updateItem', {
                                                uuid: section.uuid,
                                                field: 'displayStatus',
                                                value: { editing: false },
                                            });
                                        }, 100);
                                    }
                                });
                            }, 100);
                        }
                    } catch (_e) {}

                    store.dispatch('AutoSave/setSaved', mutation);
                })
                .catch(_error => {});
        },
    },

    'SectionTemplates/AUTO_SAVE': {
        fields: ['type'],
        callback: (store, mutation) => {
            store.dispatch('AutoSave/setSaving', mutation);

            store
                .dispatch('AutoSave/sections', {
                    mutation: mutation.type,
                    section: mutation.payload.section,
                })
                .then(() => {
                    store.dispatch('AutoSave/setSaved', mutation);
                });
        },
    },

    'SectionTemplates/SETTINGS': {
        fields: ['week_visible', 'day_visible', 'time_visible', 'remove_visible_at'],
        callback: (store, mutation) => {
            store.dispatch('AutoSave/setSaving', mutation);

            const settings = mutation.payload.type;

            const remove = mutation.payload.field === 'remove_visible_at';

            const section = cloneDeep(store.getters[`${namespace(mutation.type)}/get`](mutation.payload.uuid));

            if (!remove) {
                if (!section[settings].day_visible || !section[settings].time_visible) {
                    store.dispatch('AutoSave/setSaved', mutation);

                    return;
                }
            }

            if (section[settings].time_visible && !remove) {
                section[settings].time_visible = parseTimeString(section[settings].time_visible);
            }

            axios
                .put(`/api/section-settings-templates/${section[settings].id}`, section[settings])
                .then(() => {
                    store.dispatch('AutoSave/setSaved', mutation);
                })
                .catch(() => {
                    store.dispatch('AutoSave/setSaved', mutation);
                });
        },
    },

    'Sections/SETTINGS': {
        fields: ['visible_at', 'remove_visible_at'],
        callback: (store, mutation) => {
            store.dispatch('AutoSave/setSaving', mutation);

            const section = cloneDeep(store.getters[`${namespace(mutation.type)}/get`](mutation.payload.uuid));

            const settings = mutation.payload.type;

            const remove = mutation.payload.field === 'remove_visible_at';

            if (!remove) {
                section[settings][mutation.payload.field] = moment
                    .utc(mutation.payload.value)
                    .format('YYYY-MM-DD HH:mm:ss');
            }

            axios
                .put(`/api/section-settings/${section[settings].id}`, section[settings])
                .then(() => {
                    store.dispatch('AutoSave/setSaved', mutation);
                })
                .catch(_error => {
                    store.dispatch('AutoSave/setSaved', mutation);
                });
        },
    },

    'Sections/EDIT': {
        fields: ['title', 'description', 'prerequisite_uuid'],
        callback: (store, mutation) => {
            const section = store.getters['Sections/get'](mutation.payload.uuid);

            if (!section) return;

            // Prevent persisting empty sections in courses as well
            if (!section.title || (typeof section.title === 'string' && section.title.trim() === '')) {
                return;
            }

            store.dispatch('AutoSave/setSaving', mutation);

            store
                .dispatch('AutoSave/sections', {
                    mutation: mutation.type,
                    section,
                })
                .then(() => {
                    // After single section save, clear "newly created" styling by turning off editing
                    store.dispatch('Sections/updateItem', {
                        uuid: section.uuid,
                        field: 'displayStatus',
                        value: { editing: false },
                    });
                    // Refresh entire course to sync nested items and UI state
                    try {
                        const isTemplate = (section.type || '').toLowerCase().includes('temp');
                        const courseId = isTemplate ? section.course_template_id : section.course_id;
                        const endpoint = isTemplate ? 'course-templates' : 'courses';
                        if (courseId) {
                            axios.get(`/api/${endpoint}/${courseId}?menu=true`).then(resp => {
                                if (resp.data?.data) {
                                    store.dispatch('CourseTemplateForm/hydrate', { payload: resp.data.data });
                                }
                            });
                        }
                    } catch (_e) {
                        // no-op
                    }
                    store.dispatch('AutoSave/setSaved', mutation);
                });
        },
    },

    'Sections/AUTO_SAVE': {
        fields: ['type'],
        callback: (store, mutation) => {
            store.dispatch('AutoSave/setSaving', mutation);

            store
                .dispatch('AutoSave/sections', {
                    mutation: mutation.type,
                    section: mutation.payload.section,
                })
                .then(() => {
                    store.dispatch('AutoSave/setSaved', mutation);
                });
        },
    },
};
